import { Fragment, useMemo } from 'react';
import { getText } from 'i18n/intl';
import { Form, Select, TreeSelect } from '@timeedit/ui-components/antd';
import {
  type BulkEditAction,
  BulkEditMatchRule,
  type BulkEditTreeSelectFieldType,
  type CommonTreeSelectProps,
  getBulkEditFieldOptions,
  getBulkEditTreeSelectProps,
} from './common';

type Props = CommonTreeSelectProps & {
  actionList?: BulkEditAction[];
  allowMultipleSearch?: boolean;
};

const BulkEditTreeSelect = ({
  field,
  form,
  actionList,
  allowMultipleSearch,
}: Props) => {
  const value = Form.useWatch(field.id, form);

  const showAdd = value === 'add' && field.multiple;
  const showFindAndReplace = value === 'findReplace';
  const showReplaceAll = value === 'replaceAll';
  const showRemoveSpecific = value === 'removeSpecific';

  const options = useMemo(
    () =>
      getBulkEditFieldOptions(
        actionList ?? [
          'add',
          'keep',
          'findReplace',
          'replaceAll',
          'removeSpecific',
          'removeAll',
        ],
        field
      ),
    [actionList, field.multiple]
  );

  return (
    <div className="bulk-edit-input-wrapper">
      <Form.Item required name={field.id}>
        <Select defaultValue="keep" options={options} />
      </Form.Item>
      {showAdd && (
        <Add field={field} allowMultipleSearch={allowMultipleSearch} />
      )}
      {showFindAndReplace && (
        <FindAndReplace
          field={field}
          allowMultipleSearch={allowMultipleSearch}
        />
      )}
      {showReplaceAll && (
        <ReplaceAll field={field} allowMultipleSearch={allowMultipleSearch} />
      )}
      {showRemoveSpecific && (
        <RemoveSpecific
          field={field}
          allowMultipleSearch={allowMultipleSearch}
        />
      )}
    </div>
  );
};

type SubProps = {
  field: BulkEditTreeSelectFieldType;
  allowMultipleSearch?: boolean;
};

type TreeSelectFieldProps = SubProps & {
  name: string;
  placeholder: string;
  requiredMessage: string;
  label?: string;
  includeCommonProps?: boolean;
};

type FieldConfig = Omit<TreeSelectFieldProps, 'field' | 'allowMultipleSearch'>;

const TreeSelectField = ({
  name,
  placeholder,
  requiredMessage,
  field,
  label,
  allowMultipleSearch = false,
  includeCommonProps = false,
}: TreeSelectFieldProps) => (
  <Form.Item
    name={name}
    label={label}
    rules={[{ required: true, message: requiredMessage }]}
  >
    <TreeSelect
      placeholder={placeholder}
      allowClear={false}
      treeData={field.treeData ?? []}
      treeNodeFilterProp="title"
      treeDefaultExpandAll
      multiple={field.multiple || allowMultipleSearch ? true : undefined}
      {...(includeCommonProps ? getBulkEditTreeSelectProps(field) : {})}
    />
  </Form.Item>
);

const addFieldConfig: FieldConfig = {
  name: '_add',
  requiredMessage: 'New values required',
  placeholder: getText('object_manager.bulk-edit-modal.add.placeholder'),
  includeCommonProps: true,
};

const replaceAllFieldConfig: FieldConfig = {
  name: '_replaceAll',
  requiredMessage: 'New value(s) required',
  placeholder: getText('object_manager.bulk-edit-modal.replaceAll.placeholder'),
  includeCommonProps: true,
};

const removeSpecificFieldConfig: FieldConfig = {
  name: '_removeSpecific',
  requiredMessage: 'Value(s) to remove required',
  placeholder: getText(
    'object_manager.bulk-edit-modal.removeSpecific.placeholder'
  ),
};

const findReplaceFieldConfigs: FieldConfig[] = [
  {
    name: '_replace',
    requiredMessage: 'Value to find required',
    label: getText(
      'object_manager.bulk-edit-modal.findReplace.find-input-label'
    ),
    placeholder: getText(
      'object_manager.bulk-edit-modal.findReplace.findPlaceholder'
    ),
  },
  {
    name: '_with',
    requiredMessage: 'Replacement value(s) required',
    label: getText(
      'object_manager.bulk-edit-modal.findReplace.replace-input-label'
    ),
    placeholder: getText(
      'object_manager.bulk-edit-modal.findReplace.replacePlaceholder'
    ),
    includeCommonProps: true,
  },
];

const renderTreeSelectField = (
  field: BulkEditTreeSelectFieldType,
  allowMultipleSearch: boolean | undefined,
  config: FieldConfig
) => (
  <TreeSelectField
    field={field}
    allowMultipleSearch={allowMultipleSearch}
    name={`${field.id}${config.name}`}
    label={config.label}
    requiredMessage={config.requiredMessage}
    placeholder={config.placeholder}
    includeCommonProps={config.includeCommonProps}
  />
);

const Add = ({ field, allowMultipleSearch }: SubProps) =>
  renderTreeSelectField(field, allowMultipleSearch, addFieldConfig);

const FindAndReplace = ({ field, allowMultipleSearch }: SubProps) => (
  <>
    <BulkEditMatchRule fieldId={field.id} isMultiple={field.multiple} />
    {findReplaceFieldConfigs.map((config) => (
      <Fragment key={`${field.id}${config.name}`}>
        {renderTreeSelectField(field, allowMultipleSearch, config)}
      </Fragment>
    ))}
  </>
);

const ReplaceAll = ({ field, allowMultipleSearch }: SubProps) =>
  renderTreeSelectField(field, allowMultipleSearch, replaceAllFieldConfig);

const RemoveSpecific = ({ field, allowMultipleSearch }: SubProps) =>
  renderTreeSelectField(field, allowMultipleSearch, removeSpecificFieldConfig);

export default BulkEditTreeSelect;
